import { useCan } from '@refinedev/core';
import { useEffect, useState } from 'react';
import { useBranding } from '../../branding/BrandingProvider';
import {
  Alert,
  Badge,
  Button,
  Card,
  Field,
  Notice,
  StatusDot,
  Toasts,
  useToasts,
} from '../../components/ui';
import { EMPTY_BRANDING, type Branding } from '../../data/admin';

/**
 * Branding del tenant (RF-21, PRD §12 white-label).
 *
 * `src/index.css` declara el tema como variables CSS justo para que un inquilino
 * pueda cambiarlo **en tiempo de ejecución** sin recompilar el panel. Aquí se
 * editan y se aplican en cuanto se tocan: la vista previa no es una maqueta al
 * lado, es el panel entero cambiando de color, que es la única forma de ver si el
 * gris corporativo deja legible el texto sobre la superficie.
 *
 * Los colores se piden por separado para claro y para oscuro. Un solo par no
 * funciona: el azul que se lee bien sobre blanco desaparece sobre negro, y un
 * panel que se usa a las tres de la mañana en una tienda va a estar en oscuro.
 * Dejar un campo vacío significa «no lo toques», y se mantiene el valor del tema
 * por omisión.
 */

const FIELDS: Array<{ key: keyof Branding; label: string; hint: string; placeholder: string }> = [
  {
    key: 'brandColor',
    label: 'Color de marca',
    hint: 'Botones primarios, líneas de la gráfica y elemento activo. Tiene que contrastar en claro y en oscuro.',
    placeholder: 'oklch(0.62 0.19 255) · #2563eb',
  },
  {
    key: 'surfaceLight',
    label: 'Fondo (tema claro)',
    hint: 'Casi blanco. Un fondo saturado cansa en una jornada de ocho horas.',
    placeholder: 'oklch(0.99 0 0) · #fbfbfd',
  },
  {
    key: 'textLight',
    label: 'Texto (tema claro)',
    hint: 'Casi negro.',
    placeholder: 'oklch(0.22 0.01 260) · #1d2127',
  },
  {
    key: 'surfaceDark',
    label: 'Fondo (tema oscuro)',
    hint: 'Gris muy oscuro antes que negro puro: el negro absoluto hace vibrar el texto claro.',
    placeholder: 'oklch(0.19 0.01 260) · #16191f',
  },
  {
    key: 'textDark',
    label: 'Texto (tema oscuro)',
    hint: 'Casi blanco.',
    placeholder: 'oklch(0.94 0.01 260) · #e8eaed',
  },
];

export function BrandingPage() {
  const { data: canEdit } = useCan({ resource: 'branding', action: 'edit' });
  const { branding, source, backendNotice, preview, save, reset } = useBranding();

  const [draft, setDraft] = useState<Branding>(branding);
  const [busy, setBusy] = useState(false);
  const toasts = useToasts();

  // El branding real puede llegar del backend después de montar la pantalla.
  useEffect(() => {
    setDraft(branding);
  }, [branding]);

  // La vista previa se suelta al salir: si no, un borrador a medio hacer se
  // quedaría aplicado en el resto del panel.
  useEffect(() => () => preview(null), [preview]);

  const update = (changes: Partial<Branding>) => {
    const next = { ...draft, ...changes };
    setDraft(next);
    preview(next);
  };

  const onLogoFile = async (file: File) => {
    // Se convierte a `data:` en el navegador: el OCC no tiene endpoint de subida
    // de imágenes, y una URL externa metería una petición a un tercero en cada
    // carga del panel de un cliente.
    const reader = new FileReader();
    const dataUrl = await new Promise<string>((resolve, reject) => {
      reader.onload = () => resolve(String(reader.result ?? ''));
      reader.onerror = () => reject(new Error('No se pudo leer la imagen'));
      reader.readAsDataURL(file);
    });
    update({ logoUrl: dataUrl });
  };

  const submit = async () => {
    setBusy(true);
    try {
      await save(draft);
      toasts.ok('Branding aplicado.');
    } catch (error) {
      toasts.fail(error instanceof Error ? error.message : 'No se pudo guardar el branding');
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="grid gap-4">
      <Toasts toasts={toasts.toasts} />

      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold tracking-tight">Branding del tenant</h1>
          <p className="text-sm opacity-60">
            Nombre, logo y colores del panel. Se aplican al escribir, para que se vean antes de
            guardarlos.
          </p>
        </div>
        <Badge tone={source === 'backend' ? 'ok' : 'warn'}>
          {source === 'backend'
            ? 'guardado en el OCC'
            : source === 'local'
              ? 'solo en este navegador'
              : 'tema por omisión'}
        </Badge>
      </div>

      {!canEdit?.can && (
        <Alert>{canEdit?.reason ?? 'Solo un administrador del tenant edita el branding.'}</Alert>
      )}

      {backendNotice && <Notice tone="pending">{backendNotice}</Notice>}

      <div className="grid gap-4 lg:grid-cols-[1fr_20rem]">
        <Card title="Identidad">
          <div className="grid gap-4">
            <Field
              label="Nombre del producto"
              value={draft.productName}
              placeholder="Omnia Control Center"
              hint="Sustituye el título de la barra lateral. Vacío = el nombre por omisión."
              onChange={(event) => update({ productName: event.target.value })}
            />

            <div className="grid gap-2">
              <Field
                label="Logo (URL o data:)"
                value={draft.logoUrl}
                placeholder="https://cliente.example/logo.svg"
                hint="Se pinta a 24 px de alto en la barra lateral. Un SVG o un PNG con fondo transparente."
                onChange={(event) => update({ logoUrl: event.target.value })}
              />
              <label className="text-sm">
                <span className="opacity-70">…o sube un archivo</span>
                <input
                  type="file"
                  accept="image/*"
                  className="mt-1 block w-full text-xs"
                  onChange={(event) => {
                    const [file] = Array.from(event.target.files ?? []);
                    if (file) void onLogoFile(file);
                  }}
                />
              </label>
            </div>

            {FIELDS.map((field) => (
              <div key={field.key} className="flex items-end gap-3">
                <div className="flex-1">
                  <Field
                    label={field.label}
                    value={String(draft[field.key])}
                    placeholder={field.placeholder}
                    hint={field.hint}
                    onChange={(event) =>
                      update({ [field.key]: event.target.value } as Partial<Branding>)
                    }
                  />
                </div>
                {/* Muestra del color tal como lo interpreta el navegador: un valor
                    inválido se queda transparente y se ve al instante. */}
                <span
                  aria-hidden
                  title={String(draft[field.key]) || 'sin definir'}
                  className="mb-1 size-8 shrink-0 rounded border border-black/15 dark:border-white/20"
                  style={{ background: String(draft[field.key]) || 'transparent' }}
                />
              </div>
            ))}

            <div className="flex flex-wrap gap-2">
              <Button
                variant="primary"
                disabled={busy || !canEdit?.can}
                onClick={() => void submit()}
              >
                {busy ? 'Guardando…' : 'Guardar y aplicar'}
              </Button>
              <Button
                variant="ghost"
                disabled={busy}
                onClick={() => {
                  setDraft(branding);
                  preview(null);
                }}
              >
                Descartar cambios
              </Button>
              <Button
                variant="danger"
                disabled={busy || !canEdit?.can}
                onClick={() => {
                  setDraft(EMPTY_BRANDING);
                  void reset();
                }}
              >
                Volver al tema por omisión
              </Button>
            </div>
          </div>
        </Card>

        <BrandingPreview branding={draft} />
      </div>
    </section>
  );
}

/**
 * Muestra de los elementos que el branding toca.
 *
 * Hereda las variables ya aplicadas al documento, así que no repite los colores:
 * si el cuadro se ve mal, el panel se ve mal.
 */
function BrandingPreview({ branding }: { branding: Branding }) {
  return (
    <Card title="Vista previa">
      <div className="grid gap-3">
        <div className="flex items-center gap-2">
          {branding.logoUrl ? (
            <img src={branding.logoUrl} alt="" className="h-6 w-auto" />
          ) : (
            <span className="size-6 rounded bg-[var(--occ-brand)]" aria-hidden />
          )}
          <span className="font-semibold tracking-tight">
            {branding.productName || 'Omnia Control Center'}
          </span>
        </div>

        <div className="flex flex-wrap gap-2">
          <Button variant="primary">Primario</Button>
          <Button variant="secondary">Secundario</Button>
          <Button variant="danger">Destructivo</Button>
        </div>

        <div className="flex flex-wrap items-center gap-3 text-sm">
          <StatusDot status="online" withLabel />
          <StatusDot status="offline" withLabel />
          <Badge tone="ok">MFA</Badge>
          <Badge tone="warn">riesgo</Badge>
        </div>

        <div>
          <div className="mb-1 flex justify-between text-sm">
            <span className="opacity-70">CPU</span>
            <span>41 %</span>
          </div>
          <div className="h-1.5 rounded bg-black/10 dark:bg-white/10">
            <div className="h-full w-[41%] rounded bg-[var(--occ-brand)]" />
          </div>
        </div>

        <p className="text-sm opacity-70">
          Texto secundario sobre la superficie. Si esto no se lee cómodo, el contraste entre fondo y
          texto es insuficiente.
        </p>
      </div>
    </Card>
  );
}
